import { Box, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import BuildIcon from "@material-ui/icons/Build";
import {
	CalendarEntry,
	getCalendarEntryViewKey,
	getScheduleEntryTypeIds,
} from "Components/Calendar/CalendarEntryHelper";
import CalendarGroupRows from "Components/Calendar/CalendarGroupRows";
import {
	CellSize,
	getSplittedScheduleEntryTypeIds,
	getSplittedScheduleGroupIds,
	useIsCalendarSubHeadOpen,
} from "Components/Calendar/CalendarHelper";
import CalendarMemberRows from "Components/Calendar/CalendarMemberRows";
import CalendarNameColumn from "Components/Calendar/CalendarNameColumn";
import CalendarResourceRows from "Components/Calendar/CalendarResourceRows";
import CalenderRootView from "Components/Calendar/CalendarRootView";
import CalendarRow from "Components/Calendar/CalendarRow";
import CalendarSchedulePhaseRows, {
	CalendarSchedulePhaseRow,
} from "Components/Calendar/CalendarSchedulePhaseRows";
import CalendarWeekDropAreaView from "Components/CalendarWeek/CalendarWeekDropAreaView";
import CalendarWeekEntryView from "Components/CalendarWeek/CalendarWeekEntryView";
import { verifyError } from "Components/Common/helpers/verifyError";
import { useCalendar } from "Components/Common/hooks/useCalendar";
import {
	useEnabledWorkingDaysLength,
	useIsIsoWeekdayEnabled,
} from "Components/CompanySetting/CompanySettingHelper";
import { useCompanySetting } from "Components/CompanySetting/hooks/useCompanySetting";
import {
	CalendarEntryUpdate,
	getAcceptedDropTypes,
	getDropAreaViewKey,
	getWeekCalendarEntriesDropAreas,
	getWeekSchedulePhaseDropArea,
} from "Components/DropArea/DropAreaHelper";
import {
	ValidMember,
	displayEmployeeName,
} from "Components/Members/MembersHelper";
import { useIsInternalAdmin } from "Components/Members/hooks/useIsInternalAdmin";
import { getIsMultiWeekEnabled } from "Components/ProfileLimit/helpers/getIsMultiWeekEnabled";
import { useProfileLimits } from "Components/ProfileLimit/hooks/useProfileLimits";
import MultiWeekDisabledView from "Components/ProfileLimit/views/MultiWeekDisabledView";
import { useProjects } from "Components/Project/hooks/useProjects";
import {
	getScheduleEntryResizePixelSet,
	getSchedulePhaseResizePixelSet,
	getWeekBlockingResizePixel,
	getWeekScheduleEntriesResizePixelSet,
} from "Components/Resize/ResizeHelper";
import { useSchedule } from "Components/Schedule/hooks/useSchedule";
import { useScheduleEntries } from "Components/ScheduleEntry/hooks/useScheduleEntries";
import { useScheduleEntryModal } from "Components/ScheduleEntry/hooks/useScheduleEntryModal";
import { useScheduleEntryTypes } from "Components/ScheduleEntryType/hooks/useScheduleEntryTypes";
import { getPartialSchedulePhaseFromCalendarEntry } from "Components/SchedulePhase/SchedulePhaseHelper";
import { useSchedulePhaseModal } from "Components/SchedulePhase/hooks/useSchedulePhaseModal";
import { useSchedulePhases } from "Components/SchedulePhase/hooks/useSchedulePhases";
import { createScheduleResource } from "Components/ScheduleResource/ScheduleResourceHelper";
import { useScheduleResourceModal } from "Components/ScheduleResource/hooks/useScheduleResourceModal";
import { getFirstPaidDay } from "Components/ProfileLimit/helpers/getFirstPaidDay";
import { useTutorialModal } from "Components/Tutorial/hooks/useTutorialModal";
import { useVisibleMinutes } from "Components/WorkingHours/hooks/useVisibleMinutes";
import { color } from "Configs/theme";
import {
	FALLBACK_DROP_AREA_COLOR_HEX,
	getColorHex,
	getColorHexFromDropArea,
	getColorHexFromScheduleEntry,
} from "Helpers/ColorHelper";
import { getCalendarEntryViewTooltipText } from "Helpers/TooltipHelper";
import { useApp } from "Providers/AppProvider";
import ErrorView from "Views/ErrorView";
import LoadingIndicator from "Views/LoadingIndicator";
import MainActionButton from "Views/MainActionButton";
import { Project, ScheduleResource } from "domain-entities";
import { useSnackbar } from "material-ui-snackbar-provider";
import moment from "moment";
import React, { FC, Fragment } from "react";
import { v4 } from "uuid";
import {
	getIsWeekSingleRow,
	getWeekCalendarEntries,
	getWeekSchedulePhasesResizePixelSet,
	isWeekCalendarEntryStartingInsideColumn,
	isWeekDropAreaStartingInsideColumn,
} from "./CalendarWeekHelper";
import { DropArea } from "Components/DropArea/types/DropArea";

const weekdays = Array(7)
	.fill(null)
	.map((_, index) => index);

interface Props {
	cellSize: CellSize;
}

const CalendarWeekView: FC<Props> = (props) => {
	const isInternalAdmin = useIsInternalAdmin();
	const companySettingStatuses = useCompanySetting(
		(state) => state.item?.status
	);
	const startOfWeek = useCalendar((state) => state.startOfWeek);
	const weekPlus = useCalendar((state) => state.weekPlus);
	const isIsoWeekdayEnabled = useIsIsoWeekdayEnabled();
	const { isNative } = useApp();
	const profileLimits = useProfileLimits((state) => state.items);
	const scheduleEntries = useScheduleEntries((state) => state.items);
	const workingMinutes = useCalendar((state) => state.workingMinutes);
	const visibleMinutes = useVisibleMinutes();
	const scheduleEntryTypes = useScheduleEntryTypes((state) => state.items);
	const isInitializingScheduleEntryTypes = useScheduleEntryTypes(
		(state) => state.isInitializing
	);
	const scheduleEntryTypesError = useScheduleEntryTypes((state) => state.error);
	const projects = useProjects((state) => state.projects);
	const cellSize = props.cellSize;
	const firstPaidDay = getFirstPaidDay();
	const classes = useStyles({
		dayWidth: cellSize.week.dayWidth,
		nameWidth: cellSize.week.nameWidth,
	});

	const snackbar = useSnackbar();
	const scheduleGroups = useSchedule((state) => state.item?.groups);
	const { isCalendarSubHeadOpen, setIsCalendarSubHeadOpen } =
		useIsCalendarSubHeadOpen();
	const enabledWorkingDaysLength = useEnabledWorkingDaysLength();
	const endOfWeek = startOfWeek.clone().add(enabledWorkingDaysLength, "days");

	const schedulePhasesResizePixelSet = getWeekSchedulePhasesResizePixelSet(
		startOfWeek,
		cellSize
	);

	const scheduleEntriesResizePixelSet = getWeekScheduleEntriesResizePixelSet(
		visibleMinutes,
		workingMinutes,
		startOfWeek,
		cellSize
	);

	const weekCalendarEntries = getWeekCalendarEntries(
		scheduleEntries,
		startOfWeek
	);

	const { memberScheduleEntryTypeIds, resourceScheduleEntryTypeIds } =
		getSplittedScheduleEntryTypeIds(scheduleEntryTypes);

	const { scheduleGroupMemberIds, scheduleGroupResourceIds } =
		getSplittedScheduleGroupIds(scheduleGroups);

	function renderSchedulePhaseDropArea(
		dropArea: DropArea,
		colorHex: string,
		project?: Project
	) {
		return (
			<CalendarWeekDropAreaView
				dropArea={dropArea}
				cellSize={cellSize}
				visibleMinutes={visibleMinutes}
				scheduleEntryTypeIndex={-1}
				colorHex={colorHex}
				acceptedDropTypes={[]}
				isFullDay
				onClick={() =>
					useSchedulePhaseModal.getState().openWithItem(
						{
							project,
							forSchedulePhase: {
								start: dropArea.start,
								end: dropArea.end,
							},
						},
						true
					)
				}
			/>
		);
	}

	function renderEmptySchedulePhaseRow() {
		return weekdays.map((weekday) => {
			const day = startOfWeek.clone().add(weekday, "days");

			if (isIsoWeekdayEnabled(day.isoWeekday())) {
				return (
					<Box key={weekday} className={getDayColumnClass(day)}>
						{isInternalAdmin &&
							renderSchedulePhaseDropArea(
								{ start: day, end: day },
								FALLBACK_DROP_AREA_COLOR_HEX
							)}
					</Box>
				);
			} else {
				return null;
			}
		});
	}

	function getDayColumnClass(day: moment.Moment) {
		return [6, 7].includes(day.isoWeekday())
			? `${classes.dayColumn} ${classes.weekendColumn}`
			: classes.dayColumn;
	}

	function renderSchedulePhaseRow(row: CalendarSchedulePhaseRow) {
		const colorHex = getColorHex(row.project.colorTag);

		return weekdays.map((weekday) => {
			const day = startOfWeek.clone().add(weekday, "days");

			if (isIsoWeekdayEnabled(day.isoWeekday())) {
				const rowItem = row.items.find((item) =>
					isWeekCalendarEntryStartingInsideColumn(
						startOfWeek,
						weekday,
						item.calendarEntry
					)
				);

				const dropArea = isInternalAdmin
					? getWeekSchedulePhaseDropArea(
							day,
							row.items.map((item) => item.schedulePhase),
							row.project.id
					  )
					: undefined;

				const schedulePhaseResizePixelSet = getSchedulePhaseResizePixelSet(
					schedulePhasesResizePixelSet,
					row.items.map((item) => item.schedulePhase),
					rowItem?.schedulePhase
				);

				return (
					<Box key={weekday} className={getDayColumnClass(day)}>
						{dropArea &&
							renderSchedulePhaseDropArea(dropArea, colorHex, row.project)}
						{rowItem?.calendarEntry && (
							<CalendarWeekEntryView
								calendarEntry={rowItem.calendarEntry}
								onResizeCalendarEntry={async (calendarEntry) => {
									try {
										await useSchedulePhases
											.getState()
											.updateItem(
												getPartialSchedulePhaseFromCalendarEntry(calendarEntry)
											);
									} catch (error) {
										snackbar.showMessage(verifyError(error).message);
									}
								}}
								colorHex={colorHex}
								scheduleEntryTypeIndex={-1}
								projects={projects}
								cellSize={cellSize}
								weekday={weekday}
								visibleMinutes={visibleMinutes}
								resizePixelSet={schedulePhaseResizePixelSet}
								dropType="project"
								selectCalendarEntry={() =>
									useSchedulePhaseModal.getState().openWithItem({
										project: row.project,
										schedulePhase: rowItem.schedulePhase,
									})
								}
								isFullDay
								isNew
								tooltip={getCalendarEntryViewTooltipText({
									projectName: projects.find(
										(project) => project.id === rowItem.schedulePhase.projectId
									)?.name,
									phaseStatusName:
										companySettingStatuses?.find(
											(status) => status.id === rowItem.schedulePhase.statusId
										)?.name || "-",
								})}
							/>
						)}
					</Box>
				);
			} else {
				return null;
			}
		});
	}

	function renderScheduleEntryColumns(
		calendarEntries: CalendarEntry[],
		scheduleEntryTypeIds: string[],
		categoryScheduleEntryTypeIds: string[],
		calendarEntryUpdate: CalendarEntryUpdate,
		dropAreas: DropArea[],
		isSingleRow: boolean,
		isMultiWeekEnabled?: boolean
	) {
		return weekdays.map((weekday, index) => {
			const day = startOfWeek.clone().add(weekday, "days");

			if (isIsoWeekdayEnabled(day.isoWeekday())) {
				const isPaidColumn =
					!isMultiWeekEnabled && day.isSameOrAfter(firstPaidDay);
				const isFirstPaidColumn =
					!isMultiWeekEnabled &&
					(day.isSame(firstPaidDay) || (isPaidColumn && index === 0));

				const dayCalendarEntries = isPaidColumn
					? []
					: calendarEntries.filter((scheduleEntry) =>
							isWeekCalendarEntryStartingInsideColumn(
								startOfWeek,
								weekday,
								scheduleEntry
							)
					  );

				const dayDropAreas = isPaidColumn
					? []
					: dropAreas.filter((dropArea) =>
							isWeekDropAreaStartingInsideColumn(startOfWeek, weekday, dropArea)
					  );

				return (
					<Box key={weekday} className={getDayColumnClass(day)}>
						{dayDropAreas.map((dropArea) => {
							const colorHex = getColorHexFromDropArea(
								dropArea,
								scheduleEntryTypes
							);

							const scheduleEntryTypeIndex = scheduleEntryTypeIds.findIndex(
								(typeId) => typeId === dropArea.typeId
							);

							const acceptedDropTypes = getAcceptedDropTypes(
								dropArea,
								scheduleEntryTypeIds,
								categoryScheduleEntryTypeIds
							);

							return (
								<CalendarWeekDropAreaView
									key={`${getDropAreaViewKey(dropArea, acceptedDropTypes)}`}
									dropArea={dropArea}
									cellSize={cellSize}
									visibleMinutes={visibleMinutes}
									scheduleEntryTypeIndex={scheduleEntryTypeIndex}
									colorHex={colorHex}
									acceptedDropTypes={acceptedDropTypes}
									isSingleRow={isSingleRow}
									calendarEntryUpdate={calendarEntryUpdate}
									updateScheduleEntry={async (scheduleEntry) => {
										try {
											await useScheduleEntries.getState().updateItem({
												...scheduleEntry,
												...calendarEntryUpdate,
												startTime: dropArea.start.unix(),
												endTime: dropArea.end.unix(),
											});
										} catch (error) {
											snackbar.showMessage(verifyError(error).message);
										}
									}}
									addScheduleEntry={async (scheduleEntry) => {
										try {
											await useScheduleEntries.getState().setItem({
												...scheduleEntry,
												...calendarEntryUpdate,
												id: v4(),
												startTime: dropArea.start.unix(),
												endTime: dropArea.end.unix(),
											});

											setTimeout(() => {
												useTutorialModal.getState().openWithItem("resize");
											}, 200);
										} catch (error) {
											snackbar.showMessage(verifyError(error).message);
										}
									}}
									onClick={() =>
										useScheduleEntryModal.getState().openWithItem(
											{
												id: v4(),
												typeId: dropArea.typeId,
												startTime: dropArea.start.unix(),
												endTime: dropArea.end.unix(),
												...calendarEntryUpdate,
											},
											true
										)
									}
								/>
							);
						})}
						{dayCalendarEntries.map((item) => {
							const blockingResizePixel = getWeekBlockingResizePixel(
								item,
								calendarEntries,
								visibleMinutes,
								cellSize
							);

							const resizePixelSet = getScheduleEntryResizePixelSet(
								scheduleEntriesResizePixelSet,
								blockingResizePixel
							);

							const scheduleEntryTypeIndex = scheduleEntryTypeIds.findIndex(
								(typeId) => typeId === item.typeId
							);

							const colorHex = getColorHexFromScheduleEntry(
								item,
								projects,
								scheduleEntryTypes
							);

							return (
								<CalendarWeekEntryView
									key={getCalendarEntryViewKey(item, isSingleRow)}
									calendarEntry={item}
									onResizeCalendarEntry={async (scheduleEntry) => {
										try {
											await useScheduleEntries
												.getState()
												.updateItem(scheduleEntry);
										} catch (error) {
											snackbar.showMessage(verifyError(error).message);
										}
									}}
									colorHex={colorHex}
									scheduleEntryTypeIndex={scheduleEntryTypeIndex}
									projects={projects}
									cellSize={cellSize}
									weekday={weekday}
									visibleMinutes={visibleMinutes}
									resizePixelSet={resizePixelSet}
									dropType={item.typeId || "project"}
									selectCalendarEntry={() =>
										useScheduleEntryModal.getState().openWithItem(item)
									}
									isSingleRow={isSingleRow}
									tooltip={getCalendarEntryViewTooltipText({
										projectName: projects.find(
											(project) => project.id === item.projectId
										)?.name,
										entryTypeName: scheduleEntryTypes.find(
											({ id }) => id === item.typeId
										)?.name,
									})}
								/>
							);
						})}
						{isFirstPaidColumn && <MultiWeekDisabledView />}
					</Box>
				);
			} else {
				return null;
			}
		});
	}

	function renderMemberRow(member: ValidMember) {
		const calendarEntries = weekCalendarEntries.filter(
			(item) => item.userId === member.id
		);

		const isSingleRow = getIsWeekSingleRow(calendarEntries);
		const scheduleEntryTypeIds = getScheduleEntryTypeIds(calendarEntries);
		const calendarEntryUpdate: CalendarEntryUpdate = {
			userId: member.id,
			resourceId: undefined,
		};

		const dropAreas = isInternalAdmin
			? getWeekCalendarEntriesDropAreas(
					startOfWeek,
					calendarEntries,
					scheduleEntryTypeIds,
					visibleMinutes,
					workingMinutes
			  )
			: [];

		const isMultiWeekEnabled = getIsMultiWeekEnabled(member.id, profileLimits);
		const employeeName = displayEmployeeName(member);

		return (
			<CalendarRow
				key={member.id}
				scheduleEntryTypesLength={scheduleEntryTypeIds.length}
				isSingleRow={isSingleRow}
				renderColumns={() => (
					<Fragment>
						<CalendarNameColumn width={props.cellSize.week.nameWidth}>
							<Tooltip title={employeeName || ""} placement="top">
								<div>{employeeName}</div>
							</Tooltip>
						</CalendarNameColumn>
						{renderScheduleEntryColumns(
							calendarEntries,
							scheduleEntryTypeIds,
							memberScheduleEntryTypeIds,
							calendarEntryUpdate,
							dropAreas,
							isSingleRow,
							isMultiWeekEnabled
						)}
					</Fragment>
				)}
			/>
		);
	}

	function renderResourceRow(resource: ScheduleResource) {
		const calendarEntries = weekCalendarEntries.filter(
			(item) => item.resourceId === resource.id
		);

		const isSingleRow = getIsWeekSingleRow(calendarEntries);
		const scheduleEntryTypeIds = getScheduleEntryTypeIds(calendarEntries);
		const calendarEntryUpdate: CalendarEntryUpdate = {
			resourceId: resource.id,
			userId: undefined,
		};

		const dropAreas = isInternalAdmin
			? getWeekCalendarEntriesDropAreas(
					startOfWeek,
					calendarEntries,
					scheduleEntryTypeIds,
					visibleMinutes,
					workingMinutes
			  )
			: [];

		return (
			<CalendarRow
				key={resource.id}
				scheduleEntryTypesLength={scheduleEntryTypeIds.length}
				isSingleRow={isSingleRow}
				renderColumns={() => (
					<Fragment>
						<CalendarNameColumn
							width={props.cellSize.week.nameWidth}
							isEditable={isInternalAdmin}
							onClickEdit={() =>
								useScheduleResourceModal.getState().openWithItem(resource)
							}
						>
							<Tooltip title={resource.name || ""} placement="top">
								<div>
									<BuildIcon />
									{resource.name}
								</div>
							</Tooltip>
						</CalendarNameColumn>
						{renderScheduleEntryColumns(
							calendarEntries,
							scheduleEntryTypeIds,
							resourceScheduleEntryTypeIds,
							calendarEntryUpdate,
							dropAreas,
							isSingleRow,
							true
						)}
					</Fragment>
				)}
			/>
		);
	}

	function renderRows() {
		if (isInitializingScheduleEntryTypes) return <LoadingIndicator />;
		if (scheduleEntryTypesError) {
			return <ErrorView error={scheduleEntryTypesError} />;
		}
		return (
			<Fragment>
				<CalendarSchedulePhaseRows
					startOfCalendar={startOfWeek}
					endOfCalendar={endOfWeek}
					nameWidth={props.cellSize.week.nameWidth}
					isCalendarSubHeadOpen={isCalendarSubHeadOpen}
					setIsCalendarSubHeadOpen={setIsCalendarSubHeadOpen}
					renderEmptyRow={renderEmptySchedulePhaseRow}
					renderRow={renderSchedulePhaseRow}
				/>
				<CalendarGroupRows
					isCalendarSubHeadOpen={isCalendarSubHeadOpen}
					setIsCalendarSubHeadOpen={setIsCalendarSubHeadOpen}
					renderMemberRow={renderMemberRow}
					renderResourceRow={renderResourceRow}
				/>
				<CalendarMemberRows
					scheduleGroupMemberIds={scheduleGroupMemberIds}
					isCalendarSubHeadOpen={isCalendarSubHeadOpen}
					setIsCalendarSubHeadOpen={setIsCalendarSubHeadOpen}
					renderMemberRow={renderMemberRow}
				/>
				<CalendarResourceRows
					scheduleGroupResourceIds={scheduleGroupResourceIds}
					isCalendarSubHeadOpen={isCalendarSubHeadOpen}
					setIsCalendarSubHeadOpen={setIsCalendarSubHeadOpen}
					renderResourceRow={renderResourceRow}
				/>
			</Fragment>
		);
	}

	return (
		<Fragment>
			<CalenderRootView cellSize={props.cellSize}>
				{renderRows()}
			</CalenderRootView>
			{isInternalAdmin && !isNative && (
				<MainActionButton
					openSchedulePhaseModal={() => {
						const startDay =
							weekPlus === 0 ? moment().startOf("day") : startOfWeek.clone();

						useSchedulePhaseModal.getState().openWithItem(
							{
								forSchedulePhase: {
									start: startDay.clone(),
									end: startDay.clone(),
								},
							},
							true
						);
					}}
					openScheduleResourceModal={() => {
						useScheduleResourceModal
							.getState()
							.openWithItem(createScheduleResource(), true);
					}}
				/>
			)}
		</Fragment>
	);
};

export default CalendarWeekView;

interface StyleProps {
	nameWidth: number;
	dayWidth: number;
}

const useStyles = makeStyles({
	dayColumn: (props: StyleProps) => ({
		display: "flex",
		position: "relative",
		borderLeft: `1px solid ${color.tableCellBorder}`,
		borderBottom: `1px solid ${color.tableCellBorder}`,
		width: props.dayWidth - 1,
	}),
	weekendColumn: {
		backgroundImage:
			"url(\"data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8'%3F%3E%3Csvg width='6px' height='6px' viewBox='0 0 6 6' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cg stroke='none' stroke-width='1' fill='none' fill-rule='evenodd' stroke-linecap='square'%3E%3Cline x1='-1.13427372' y1='4.15051979' x2='4.86572628' y2='10.1505198' stroke='%23E9E8E8'%3E%3C/line%3E%3Cline x1='0.0937385908' y1='-0.617078479' x2='6.09373859' y2='5.38292152' stroke='%23E9E8E8'%3E%3C/line%3E%3C/g%3E%3C/svg%3E\")",
		backgroundSize: "6px",
		backgroundRepeat: "repeat",
	},
});
